import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon } from '../../../../../assets/icons';
import RequirementModal from '../../../../MarketPlace/Requirements/RequirementModal';
import ViewRequirementModal from '../../../../MarketPlace/Requirements/ViewRequirementModal';
import DeletePopup from '../../../../../components/delete-popup';
import { deleteMarketplaceRequirement } from '../../../../../redux/slice/marketplaceSlice';
import { fetchUserMarketplaceRequirements } from '../../../../../redux/slice/allUsersSlice';
import CustomTable from '../../../../../components/table/CustomTable';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { useToast } from '../../../../../context/TostContext';
import { IconBtn } from '../../../../../components/commonButton/btns';
import StatusChip from '../../../../../components/statusChip/StatusChip';

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "open":
            return "success";
        case "closed":
            return "secondary";
        default:
            return "";
    }
};

const Requirements = ({ userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const requirementId = searchParams.get("requirementId");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false);

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        marketplaceRequirements,
        marketplaceRequirementsLoading,
        marketplaceRequirementsTotal
    } = useSelector(state => state.allUsers);

    useEffect(() => {
        if (userDetail?.id) {
            dispatch(fetchUserMarketplaceRequirements({ id: userDetail.id, page, limit }));
        }
    }, [dispatch, userDetail?.id, page, limit]);

    useEffect(() => {
        if (requirementId && marketplaceRequirements?.length > 0) {
            const req = marketplaceRequirements.find(item => String(item.id) === String(requirementId));
            if (req) {
                setCurrentItem(req);
                setIsViewMode(true);
                setShowModal(true);
            }
        } else {
            if (isViewMode) {
                setShowModal(false);
                setCurrentItem(null);
                setIsViewMode(false);
            }
        }
    }, [requirementId, marketplaceRequirements, isViewMode]);

    const handleDelete = () => {
        setIsDeleting(true);
        dispatch(deleteMarketplaceRequirement(itemToDelete))
            .unwrap()
            .then(() => {
                toast.success("Requirement deleted successfully");
                setShowDeletePopup(false);
                setItemToDelete(null);
                dispatch(fetchUserMarketplaceRequirements({ id: userDetail.id, page, limit }));
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to delete requirement");
            })
            .finally(() => setIsDeleting(false));
    };
    const actionList = [
        {
            title: 'View', handler: (row) => {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set("requirementId", row.id);
                    return params;
                });
            }
        },
        { title: 'Edit', handler: (row) => { setCurrentItem(row); setIsViewMode(false); setShowModal(true); } },
        { title: 'Delete', handler: (row) => { setItemToDelete(row.id); setShowDeletePopup(true); }, isDanger: true },
    ];
    const columns = [

        {
            header: 'Title',
            cell: (_, item) => <div className="fw-medium text-truncate" style={{ maxWidth: "200px" }}>{item.title || "-"}</div>
        },
        {
            header: 'Category',
            cell: (_, item) => item.category?.name || "-"
        },
        {
            header: 'Location',
            cell: (_, item) => {
                const parts = [];
                if (item.city?.name) parts.push(item.city.name);
                if (item.state?.name) parts.push(item.state.name);
                return parts.length > 0 ? parts.join(", ") : "-";
            }
        },
        {
            header: 'Status',
            cell: (_, item) => (
                item.status ? (
                    <StatusChip
                        bg={getStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                )
            )
        },
        {
            header: 'Created At',
            cell: (_, item) => formatMomentDate(item.created_at, "DD-MM-YYYY hh:mm A")
        },
        {
            header: 'Updated At',
            cell: (_, item) => formatMomentDate(item.updated_at, "DD-MM-YYYY hh:mm A")
        }
    ];

    const totalPages = Math.ceil((marketplaceRequirementsTotal || 0) / limit) || 1;

    return (
        <div className="position-relative h-100">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Requirements</h6>
                <IconBtn size={32} icon={<PlusIcon size={16} />} className='rounded-circle' onClick={() => {
                    setCurrentItem(null);
                    setIsViewMode(false);
                    setShowModal(true);
                }} />

            </div>

            <div className="border rounded-3 overflow-hidden bg-white">
                <CustomTable
                    columns={columns}
                    data={marketplaceRequirements || []}
                    loading={marketplaceRequirementsLoading}
                    actionList={actionList}
                    emptyMessage="No requirements listed."
                    pagination={{
                        currentPage: page,
                        totalPages,
                        total: marketplaceRequirementsTotal,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </div>

            <RequirementModal
                isOpen={showModal && !isViewMode}
                onClose={() => {
                    setShowModal(false);
                    setCurrentItem(null);
                    setIsViewMode(false);
                }}
                editData={currentItem}
                isView={isViewMode}
                defaultUser={userDetail ? { id: userDetail.id, name: `${userDetail.first_name || ""} ${userDetail.last_name || ""}`.trim() } : null}
                onSuccess={() => dispatch(fetchUserMarketplaceRequirements({ id: userDetail.id, page, limit }))}
                title={currentItem ? "Edit Requirement" : "Add Requirement"}
                size="lg"
                className="common-modal"
            />

            <ViewRequirementModal
                isOpen={showModal && isViewMode}
                onClose={() => {
                    setShowModal(false);
                    setCurrentItem(null);
                    setIsViewMode(false);
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.delete("requirementId");
                        return params;
                    });
                }}
                data={currentItem}
                size="lg"
            />

            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Requirement"
                loading={isDeleting}
                saveTitle="Delete"
            />
        </div>
    );
};

export default Requirements;
